// ---------------------------------------------------------------------------
// GradersList — per-task grader picker, modelled on the Variables card.
//
// Graders are added by picking one from the workspace catalog in the
// "Add grader" dropdown; each selected grader then shows as a compact row
// with a remove button (like a variables list). Graders already added are
// excluded from the dropdown so the same one can't be picked twice.
//
// Inline grader authoring ("new") isn't implemented, so there's no
// existing/new toggle — selection is the whole interaction. In view mode
// the rows render as static names with no add/remove affordances.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Dropdown,
  Field,
  Option,
  Spinner,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Delete20Filled,
  Delete20Regular,
} from '@fluentui/react-icons';

import { useStyles } from '../../EvaluationTemplateForm.style.js';
import type {
  GraderSummary,
  TaskDraft,
} from '../../EvaluationTemplateForm.types.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const DeleteIcon = bundleIcon(Delete20Filled, Delete20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

export interface GradersListProps {
  task: TaskDraft;
  /** All graders available in the workspace, deduplicated by name. */
  uniqueGraders: GraderSummary[];
  gradersLoading: boolean;
  /** When true, the add dropdown + remove buttons collapse to static rows. */
  viewOnly: boolean;
  onAddGraderRow: (name: string) => void;
  onRemoveGraderRow: (rowId: string) => void;
}

export function GradersList({
  task,
  uniqueGraders,
  gradersLoading,
  viewOnly,
  onAddGraderRow,
  onRemoveGraderRow,
}: GradersListProps) {
  const styles = useStyles();

  // Graders already on this task — excluded from the add dropdown so the
  // same grader can't be added twice.
  const selectedNames = new Set(
    task.graderRows.map((r) => r.name).filter((n) => n.length > 0),
  );
  const availableToAdd = uniqueGraders.filter(
    (g) => !selectedNames.has(g.name),
  );

  return (
    <div className={styles.cardBody}>
      {gradersLoading && <Spinner size="tiny" label="Loading graders..." />}

      {task.graderRows.length === 0 ? (
        <Body1 className={styles.helperText}>
          {viewOnly ? 'No graders configured.' : 'No graders added yet.'}
        </Body1>
      ) : (
        task.graderRows.map((row) => (
          <div key={row.id} className={styles.graderChipRow}>
            <span className={styles.graderChipName}>
              {row.name || '(unnamed grader)'}
            </span>
            {!viewOnly && (
              <Tooltip content="Remove grader" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<DeleteIcon />}
                  onClick={() => onRemoveGraderRow(row.id)}
                  aria-label={`Remove grader ${row.name || row.id}`}
                />
              </Tooltip>
            )}
          </div>
        ))
      )}

      {!viewOnly &&
        (uniqueGraders.length === 0 ? (
          <Body1 className={styles.helperText}>
            No graders in this workspace yet.
          </Body1>
        ) : (
          <Field
            label="Select grader"
            orientation="horizontal"
            className={styles.inlineField}
          >
            <Dropdown
              aria-label="Select grader"
              placeholder={
                availableToAdd.length === 0
                  ? 'All graders added'
                  : 'Choose a grader'
              }
              // Always reset to the placeholder — picking an option adds it
              // to the list rather than selecting a persistent value here.
              value=""
              selectedOptions={[]}
              disabled={availableToAdd.length === 0}
              onOptionSelect={(_e, data) => {
                if (data.optionValue) {
                  onAddGraderRow(data.optionValue);
                }
              }}
            >
              {availableToAdd.map((g) => (
                // Label by resource name so the picker matches the grader list
                // rail and the task's `graders[]` (which reference by name).
                <Option key={g.name} value={g.name} text={g.name}>
                  {g.name}
                </Option>
              ))}
            </Dropdown>
          </Field>
        ))}
    </div>
  );
}
